import { Modal } from '@/components/admin/Modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { variantForTag } from '@/lib/tag-color';
import type { ResponsePromptVersion } from '@innermate/shared';
import { Check, GitCompare, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { VersionDiffModal } from './VersionDiffModal';

const PAGE_SIZE = 20;

interface Props {
  open: boolean;
  onClose: () => void;
  versions: ResponsePromptVersion[];
  currentVersion: number | null;
  activatePending: boolean;
  tagsPending: boolean;
  onActivate: (versionId: string) => void;
  onSetTags: (versionId: string, tags: string[]) => void;
}

export function VersionsModal({
  open,
  onClose,
  versions,
  currentVersion,
  activatePending,
  tagsPending,
  onActivate,
  onSetTags,
}: Props) {
  const [editingTagsFor, setEditingTagsFor] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const startEditTags = (v: ResponsePromptVersion) => {
    setEditingTagsFor(v.id);
    setTagDraft(v.tags);
    setTagInput('');
  };
  const cancelTags = () => {
    setEditingTagsFor(null);
    setTagDraft([]);
    setTagInput('');
  };
  const addTag = () => {
    const t = tagInput.trim();
    if (!t || tagDraft.includes(t)) {
      setTagInput('');
      return;
    }
    setTagDraft([...tagDraft, t]);
    setTagInput('');
  };

  const sorted = [...versions].sort((a, b) => b.version - a.version);

  // 페이지네이션 — 모달 열릴 때마다 처음으로 리셋.
  const [shown, setShown] = useState(PAGE_SIZE);
  useEffect(() => {
    if (open) setShown(PAGE_SIZE);
  }, [open]);

  const visible = sorted.slice(0, shown);
  const hasMore = sorted.length > shown;

  const activeVersionObj = sorted.find((v) => v.version === currentVersion) ?? null;
  const [diffWith, setDiffWith] = useState<ResponsePromptVersion | null>(null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`버전 히스토리${sorted.length > 0 ? ` (${sorted.length})` : ''}`}
      description="모든 변경 이력은 보존됩니다. 과거 버전으로 되돌리면 새 버전이 자동 생성됩니다."
      size="xl"
    >
      {sorted.length === 0 ? (
        <div className="py-8 text-center text-sm text-foreground-muted">
          아직 저장된 버전이 없습니다.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {visible.map((v) => {
            const isCurrent = currentVersion === v.version;
            const isEditing = editingTagsFor === v.id;
            return (
              <li
                key={v.id}
                className={cn(
                  'py-3 -mx-2 px-3 rounded-md',
                  isCurrent ? 'bg-accent/5 border border-accent' : '',
                )}
              >
                <div className="flex items-center gap-2">
                  <Badge variant={isCurrent ? 'accent' : 'outline'}>v{v.version}</Badge>
                  {isCurrent ? <span className="text-xs text-accent">현재 활성</span> : null}
                  {v.source === 'rollback' ? <Badge variant="muted">롤백</Badge> : null}
                  <div className="ml-auto text-xs text-foreground-muted">
                    {new Date(v.createdAt).toLocaleString('ko-KR')}
                    {v.createdByName ? <span className="ml-1">· {v.createdByName}</span> : null}
                  </div>
                </div>

                {v.changelog ? (
                  <div className="mt-1.5 text-sm text-foreground-muted">{v.changelog}</div>
                ) : null}

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {isEditing ? (
                    <>
                      {tagDraft.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-muted px-2 py-0.5 text-xs"
                        >
                          {t}
                          <button
                            type="button"
                            onClick={() => setTagDraft(tagDraft.filter((x) => x !== t))}
                            className="text-foreground-muted hover:text-destructive"
                            aria-label="태그 제거"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      ))}
                      <div className="flex items-center gap-1">
                        <Input
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          placeholder="태그 추가"
                          className="h-7 w-32 text-xs"
                          onKeyDown={(e) => {
                            // 한글 IME composition 중 Enter 는 무시.
                            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                              e.preventDefault();
                              addTag();
                            }
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={addTag}
                          disabled={!tagInput.trim()}
                        >
                          +
                        </Button>
                      </div>
                      <div className="ml-auto flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelTags}
                          disabled={tagsPending}
                        >
                          취소
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            onSetTags(v.id, tagDraft);
                            setEditingTagsFor(null);
                          }}
                          disabled={tagsPending}
                        >
                          <Check className="size-3.5 mr-1" />
                          저장
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {v.tags.map((t) => (
                        <Badge key={t} variant={variantForTag(t)}>
                          {t}
                        </Badge>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditTags(v)}
                        className="h-6"
                      >
                        {v.tags.length === 0 ? '태그 추가' : '태그 편집'}
                      </Button>
                      {!isCurrent ? (
                        <div className="ml-auto flex gap-1">
                          {activeVersionObj && activeVersionObj.id !== v.id ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7"
                              onClick={() => setDiffWith(v)}
                              title="현재 활성 버전과 본문 비교"
                            >
                              <GitCompare className="size-3.5 mr-1" />
                              비교
                            </Button>
                          ) : null}
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-7"
                            onClick={() => onActivate(v.id)}
                            disabled={activatePending}
                          >
                            이 버전 활성화
                          </Button>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                <details className="mt-2">
                  <summary
                    className={cn(
                      'cursor-pointer text-xs text-foreground-muted hover:text-foreground select-none',
                    )}
                  >
                    본문 미리보기
                  </summary>
                  <pre className="mt-2 max-h-60 overflow-auto rounded-md border border-border bg-surface-muted/30 p-3 text-xs leading-relaxed whitespace-pre-wrap font-mono">
                    {v.content}
                  </pre>
                </details>
              </li>
            );
          })}
        </ul>
      )}
      {hasMore ? (
        <div className="mt-3 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShown((s) => s + PAGE_SIZE)}
          >
            더 보기 ({sorted.length - shown})
          </Button>
        </div>
      ) : null}
      <VersionDiffModal
        open={diffWith !== null && activeVersionObj !== null}
        onClose={() => setDiffWith(null)}
        left={
          activeVersionObj
            ? { label: `현재 활성 v${activeVersionObj.version}`, version: activeVersionObj }
            : null
        }
        right={diffWith ? { label: `v${diffWith.version}`, version: diffWith } : null}
      />
    </Modal>
  );
}
